import {getAuth} from 'firebase/auth'
import React, {useCallback, useEffect, useState} from 'react'
import {useNotification} from '../../common/notifications/useNotification'
import {Box} from '../../webui/Box'
import {Paper} from '../../webui/Paper'
import {ButtonWithLoading} from '../../webui/ButtonWithLoading'
import {CheckboxWithLabel, CheckboxWithLabelProps} from '../../webui/inputs/CheckboxWithLabel'
import {LoadingSpinnerContainer} from '../../webui/progress/LoadingSpinnerContainer'
import {TranslationEditor} from './TranslationEditor'
import {Typography} from '../../webui/Typography'
import {getReactAppServerURL} from '../../common/environment/environmentManager'

interface Props {}

const sortObjectKeysRecursively = (data: any): any => {
  if (typeof data !== 'object' || data === null || Array.isArray(data)) {
    return data
  }
  const sortedKeys = Object.keys(data).sort()
  const result: Record<string, any> = {}
  for (const key of sortedKeys) {
    result[key] = sortObjectKeysRecursively(data[key]) // Recurse for nested objects
  }
  return result
}

export const EditTranslationsPage: React.FC<Props> = () => {
  const [languages, setLanguages] = useState<Array<{id: string; label: string}>>([])
  const [initialTranslations, setInitialTranslations] = useState<Record<string, Object | undefined>>({})
  const [translations, setTranslations] = useState<Record<string, object | undefined>>({})
  const [translationsLoading, setTranslationsLoading] = useState<number>(0)
  const [savingTranslations, setSavingTranslations] = useState(false)
  const [selectedLanguages, setSelectedLanguages] = useState<Record<string, boolean>>({})
  const [isAdminOverrideActive, setIsAdminOverrideActive] = useState(false)

  const {showNotification} = useNotification()

  const monospaceStyle = {fontFamily: 'monospace', fontSize: 12}

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    setIsAdminOverrideActive(params.has('adminoverride'))
  }, [])

  useEffect(() => {
    if (!isAdminOverrideActive) {
      setTranslations({})
    }
  }, [isAdminOverrideActive])

  // Fetch available languages dynamically (can be from API or config file)
  useEffect(() => {
    async function fetchLanguages() {
      const availableLanguages = [
        {id: 'en', label: 'English'},
        {id: 'sv', label: 'Swedish'},
        {id: 'no', label: 'Norwegian'},
        {id: 'da', label: 'Danish'},
      ]
      setLanguages(availableLanguages)
      setSelectedLanguages(availableLanguages.reduce((acc, lang) => ({...acc, [lang.id]: true}), {}))
    }

    fetchLanguages()
  }, [])

  useEffect(() => {
    async function fetchTranslations(language: string) {
      setTranslationsLoading((n) => n + 1)
      const token = await getAuth().currentUser?.getIdToken()

      const resp = await fetch(`${getReactAppServerURL()}/api/translations`, {
        headers: {
          authorization: `Bearer ${token}`,
          'accept-language': language,
          'x-client-kind': 'admin',
        },
      })
      const jsonStringPayload = await resp.json()
      const parsedObject = JSON.parse(jsonStringPayload)

      setInitialTranslations((t) => ({
        ...t,
        [language]: sortObjectKeysRecursively(parsedObject ?? {}),
      }))
      setTranslationsLoading((n) => n - 1)
    }

    languages.forEach((lang) => fetchTranslations(lang.id))
  }, [languages])

  const handleChange = useCallback(
    (lang: string) => (json: any) => {
      if (!isAdminOverrideActive) return
      setTranslations((t) => ({
        ...t,
        [lang]: json,
      }))
    },
    [isAdminOverrideActive],
  )

  if (translationsLoading > 0) {
    return <LoadingSpinnerContainer />
  }

  const handleChangeSelectedLanguage =
    (lang: string): CheckboxWithLabelProps['onChange'] =>
    (selected) => {
      setSelectedLanguages((langs) => ({
        ...langs,
        [lang]: selected,
      }))
    }

  const onSave = async () => {
    if (!isAdminOverrideActive) return

    setSavingTranslations(true)
    const successfullySavedLanguages: string[] = []
    try {
      const token = await getAuth().currentUser?.getIdToken()

      const updatedTranslations = Object.entries(translations).filter(([lang, value]) => selectedLanguages[lang] && value)

      for (const [lang, translationObject] of updatedTranslations) {
        const resp = await fetch(`${getReactAppServerURL()}/api/setTranslations`, {
          method: 'POST',
          headers: {
            authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
            'x-client-kind': 'admin',
          },
          body: JSON.stringify({
            language: lang,
            translations: JSON.stringify(translationObject),
          }),
        })

        if (resp.status === 200) {
          successfullySavedLanguages.push(lang.toUpperCase())
          setInitialTranslations((t) => ({
            ...t,
            [lang]: sortObjectKeysRecursively(translationObject),
          }))
          setTranslations((t) => ({
            ...t,
            [lang]: undefined,
          }))
        } else {
          showNotification({
            message: `Something went wrong when saving ${lang}`,
            severity: 'error',
          })
        }
      }
      if (successfullySavedLanguages.length > 0) {
        const languageListString = successfullySavedLanguages.join(', ')
        showNotification({
          message: `${languageListString} saved successfully!`,
          severity: 'success',
        })
      }
    } catch (error) {
      console.error('Error during save operation:', error)
      showNotification({
        message: 'An unexpected error occurred while saving translations.',
        severity: 'error',
      })
    } finally {
      setSavingTranslations(false)
    }
  }

  const readOnlyMode = !isAdminOverrideActive

  return (
    <Box>
      <Typography fontWeight="bold" fontSize={20} mb={1} ml={1}>
        Translations
      </Typography>
      <Paper elevation={1} sx={{p: 2, mx: 1, mb: 2}}>
        <Typography>
          This page manages temporary overrides for the app's UI texts. Ideally it should stay empty — overrides are only needed when a correction
          can't wait for the next app release.
        </Typography>
        {readOnlyMode ? (
          <Typography mt={1}>Only developers can add/edit translation overrides, talk to the dev team if you need to make a change.</Typography>
        ) : (
          <Box>
            <Typography fontWeight="bold" mt={1}>
              Edit mode is enabled
            </Typography>
            <Typography>
              Any changes saved here must also be synced to the translation files in the piffl-app repo (
              <span style={monospaceStyle}>&lt;root&gt;/locales</span>).
            </Typography>
            <Typography>
              Note the app version at the time of the override, and remove it once that version is old enough to be safely ignored.
            </Typography>
          </Box>
        )}
      </Paper>
      <Box display={'flex'} flexDirection={'row'} alignItems={'center'} mx={1} py={2} justifyContent={'space-between'}>
        <Box>
          {languages.map((lang) => (
            <CheckboxWithLabel
              key={`lang-checkbox-${lang.id}`}
              onChange={handleChangeSelectedLanguage(lang.id)}
              value={selectedLanguages[lang.id]}
              label={lang.label}
            />
          ))}
        </Box>
        <ButtonWithLoading
          loading={savingTranslations}
          disabled={readOnlyMode || languages.every((lang) => !selectedLanguages[lang.id] || !translations[lang.id])}
          onClick={onSave}
        >
          Save
        </ButtonWithLoading>
      </Box>
      <Box display={'flex'} flexDirection={'row'} flex={1}>
        {Object.entries(selectedLanguages).map(([lang, enabled]) => {
          const jsonValues = initialTranslations[lang]
          if (jsonValues === null) {
            return null
          }

          return (
            <TranslationEditor
              key={`editor-${lang}`}
              hasChanges={translations[lang] !== undefined}
              json={jsonValues}
              label={languages?.find((l) => l.id === lang)?.label ?? ''}
              lang={lang}
              onChange={handleChange}
              enabled={enabled}
              readOnly={readOnlyMode}
            />
          )
        })}
      </Box>
    </Box>
  )
}
